import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { WorkoutPlanDto } from '@wellverse/shared-ts';
import { WorkoutPlanType } from '@wellverse/shared-ts';

const planTypeColors: Record<WorkoutPlanType, string> = {
  [WorkoutPlanType.AI_SUGGESTED]: '#722ed1',
  [WorkoutPlanType.CUSTOM]: '#1890ff',
  [WorkoutPlanType.KOL_ROUTINE]: '#eb2f96',
  [WorkoutPlanType.GOAL_ROUTINE]: '#faad14',
  [WorkoutPlanType.SEVEN_DAY]: '#13c2c2',
  [WorkoutPlanType.QUICK_LOG]: '#52c41a',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const WorkoutPlanList: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  const { tableProps } = useTable<WorkoutPlanDto>({
    resource: 'workout-plans',
    filters: {
      permanent: [{ field: 'search', operator: 'contains', value: searchText }],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="workout-plans">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          dataIndex="planType"
          title="Plan Type"
          filters={Object.values(WorkoutPlanType).map((t) => ({ text: t, value: t }))}
          onFilter={(value, record: WorkoutPlanDto) => record.planType === value}
          render={(type: WorkoutPlanType) => (
            <Tag style={tagStyle(planTypeColors[type] || '#898989')}>{type}</Tag>
          )}
        />
        <Table.Column
          dataIndex="isPublic"
          title="Public"
          filters={[
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ]}
          onFilter={(value, record: WorkoutPlanDto) => record.isPublic === value}
          render={(isPublic: boolean) => (
            <Tag style={tagStyle(isPublic ? '#3ecf8e' : '#898989')}>
              {isPublic ? 'Yes' : 'No'}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="exerciseCount"
          title="# Exercises"
          render={(count: number, record: WorkoutPlanDto & { exerciseCount?: number }) =>
            count ?? record.exercises?.length ?? 0
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: WorkoutPlanDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="workout-plans" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="workout-plans" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="workout-plans" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
